/* The Good Plant Co — motion system (GSAP + ScrollTrigger + SplitText + Lenis).
   ALL choreography lives here; sections stay declarative via data attributes:

     data-split="chars|words|lines"  + data-split-anim="pop|rise|spring"
     data-reveal            fade-rise once   |  data-reveal="mask" clip reveal
     data-parallax="0.15"   inner img/video scrubs against scroll
     data-cards-in          children fly in staggered with alternating rotation
     data-float="0.06"      mouse-reactive drift (fine pointers only)
     data-motion="pinned-cards"  pinned wrapper, [data-pin-card]s scrub in

   Never put GSAP code in section {% javascript %} blocks — Shopify concatenates
   those into one file whose order vs these vendored libs isn't guaranteed. */
(() => {
  'use strict';

  if (!window.gsap || !window.ScrollTrigger || !window.SplitText) return;

  gsap.registerPlugin(ScrollTrigger, SplitText);

  const editor = !!(window.Shopify && window.Shopify.designMode);
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Lenis smooth scroll ----------
     Lenis animates the real window scroll, so ScrollTrigger's default
     scroller Just Works — no scrollerProxy. Off in the theme editor and
     for anyone who asked for reduced motion (smooth scroll is motion too). */
  let lenis = null;
  if (!editor && !reduced && window.Lenis) {
    lenis = new Lenis({ autoRaf: false, anchors: true });
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add((t) => lenis.raf(t * 1000));
    gsap.ticker.lagSmoothing(0);
    window.tgpcLenis = lenis;

    /* Dialogs (cart drawer, menu, exit nudge): halt the virtual scroll while
       one is open. 'close' doesn't bubble — capture phase catches it; the
       zero-timeout lets showModal()/close() land first. Scrollable panels
       inside dialogs opt out of Lenis with data-lenis-prevent. */
    const syncLenis = () => setTimeout(() => {
      document.querySelector('dialog[open]') ? lenis.stop() : lenis.start();
    }, 0);
    document.addEventListener('click', syncLenis);
    document.addEventListener('close', syncLenis, true);
    document.addEventListener('keydown', (e) => e.key === 'Escape' && syncLenis());
    document.addEventListener('cart:updated', syncLenis);
  }

  ScrollTrigger.config({ ignoreMobileResize: true });

  const SEL = '[data-split],[data-reveal],[data-parallax],[data-cards-in],[data-float],[data-motion]';
  const finePointer = window.matchMedia('(pointer: fine)');

  let active = false;
  let ctx; // the live gsap.matchMedia context — build() registers through it

  /* Elements already on screen play their intro immediately; anything below
     the fold waits for its own scroll trigger. Keeps no-JS/late-JS users and
     the LCP hero safe (content is only hidden by gsap.from, never CSS). */
  const inView = (el) => {
    const r = el.getBoundingClientRect();
    return r.top < innerHeight && r.bottom > 0;
  };
  const trigger = (el, extra) =>
    inView(el) ? undefined : { trigger: el, start: 'top 85%', once: true, ...extra };

  /* ---------- builders ---------- */

  const buildSplit = (el) => {
    const type = el.dataset.split === 'words' ? 'words' : el.dataset.split === 'lines' ? 'lines' : 'chars';
    /* SplitText 3.13 waits for fonts + re-splits on resize by itself
       (autoSplit), so line breaks never freeze wrong. */
    SplitText.create(el, {
      type: type === 'chars' ? 'chars,words' : type,
      mask: type === 'chars' ? 'words' : type,
      wordsClass: 'splitword',
      charsClass: 'splitchar',
      linesClass: 'splitline',
      autoSplit: true,
      aria: 'auto',
      onSplit(self) {
        const targets = type === 'chars' ? self.chars : type === 'words' ? self.words : self.lines;
        const animation = el.dataset.splitAnim || 'pop';
        if (animation === 'spring') {
          return gsap.from(targets, {
            scale: 0,
            transformOrigin: '50% 65%',
            ease: 'back.out(2.1)',
            duration: 0.7,
            stagger: 0.026,
            clearProps: 'transform,transform-origin',
            scrollTrigger: trigger(el),
          });
        }
        const pop = animation === 'pop';
        return gsap.from(targets, {
          yPercent: 115,
          rotation: pop ? () => gsap.utils.random(-8, 8) : 3,
          ease: pop ? 'back.out(1.9)' : 'power4.out',
          duration: pop ? 0.7 : 0.9,
          stagger: pop ? 0.026 : 0.085,
          scrollTrigger: trigger(el),
        });
      },
    });
  };

  const buildReveal = (el) => {
    if (el.dataset.reveal === 'mask') {
      gsap.from(el, {
        clipPath: 'inset(16% 10% 16% 10% round 24px)',
        duration: 1.1,
        ease: 'power3.inOut',
        scrollTrigger: trigger(el, { start: 'top 80%' }),
      });
    } else {
      gsap.from(el, {
        y: 44,
        autoAlpha: 0,
        duration: 0.9,
        ease: 'power3.out',
        scrollTrigger: trigger(el),
      });
    }
  };

  const buildParallax = (el) => {
    const media = el.querySelector('img, video') || el;
    const amt = (parseFloat(el.dataset.parallax) || 0.15) * 100;
    gsap.fromTo(
      media,
      { yPercent: -amt },
      {
        yPercent: amt,
        ease: 'none',
        scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: 0.5 },
      }
    );
  };

  const buildCardsIn = (el) => {
    gsap.from(el.children, {
      y: 90,
      rotation: (i) => (i % 2 ? 5 : -5),
      autoAlpha: 0,
      duration: 0.8,
      ease: 'back.out(1.4)',
      stagger: 0.09,
      clearProps: 'transform', // cards keep their own hover transforms afterwards
      scrollTrigger: trigger(el),
    });
  };

  /* One shared mousemove for every floater on the page */
  const floaters = [];
  let floatBound = false;
  let floatRaf = null;
  let floatX = 0;
  let floatY = 0;
  const buildFloat = (el) => {
    if (!finePointer.matches) return;
    floaters.push({
      strength: parseFloat(el.dataset.float) || 0.05,
      qx: gsap.quickTo(el, 'x', { duration: 0.9, ease: 'power3' }),
      qy: gsap.quickTo(el, 'y', { duration: 0.9, ease: 'power3' }),
    });
    if (floatBound) return;
    floatBound = true;
    document.addEventListener('mousemove', (e) => {
      if (!active) return;
      floatX = e.clientX;
      floatY = e.clientY;
      if (floatRaf) return;
      floatRaf = requestAnimationFrame(() => {
        floatRaf = null;
        if (!active) return;
        const dx = floatX - innerWidth / 2;
        const dy = floatY - innerHeight / 2;
        floaters.forEach((f) => {
          f.qx(dx * f.strength);
          f.qy(dy * f.strength);
        });
      });
    });
  };

  const buildPinnedCards = (el) => {
    const pinWrap = el.querySelector('[data-pin-inner]') || el.firstElementChild;
    const cards = el.querySelectorAll('[data-pin-card]');
    if (!pinWrap || !cards.length) return;
    gsap.timeline({
      scrollTrigger: {
        trigger: el,
        start: 'top top',
        end: '+=' + cards.length * 50 + '%',
        scrub: 0.5,
        /* pin an inner wrapper, never a .shopify-section grid child — the
           pin-spacer must not become the grid item. Pinning is also off in
           the editor, where section DOM swaps make spacers misbehave. */
        pin: editor ? false : pinWrap,
        anticipatePin: 1,
      },
    }).from(cards, { yPercent: 140, rotation: (i) => (i % 2 ? 4 : -4), stagger: 0.24, ease: 'none' });
  };

  const build = (el) => {
    if (el.dataset.split != null) return buildSplit(el);
    if (el.dataset.reveal != null) return buildReveal(el);
    if (el.dataset.parallax != null) return buildParallax(el);
    if (el.dataset.cardsIn != null) return buildCardsIn(el);
    if (el.dataset.float != null) return buildFloat(el);
    if (el.dataset.motion === 'pinned-cards') return buildPinnedCards(el);
  };

  /* ---------- init / re-init (theme editor safe) ---------- */

  const initScope = (scope) => {
    if (!active || !ctx) return;
    scope.querySelectorAll(SEL).forEach((el) => {
      if (el.dataset.motionOn) return; // per-element guard: document + section runs never double-bind
      el.dataset.motionOn = '1';
      ctx.add(() => build(el));
    });
    /* the editor throws section DOM away wholesale — kill triggers whose
       elements left the document, then re-measure everything */
    ScrollTrigger.getAll().forEach((st) => {
      const t = st.trigger;
      if (t && t instanceof Element && !document.contains(t)) st.kill();
    });
    requestAnimationFrame(() => ScrollTrigger.refresh());
  };

  const mm = gsap.matchMedia();
  mm.add('(prefers-reduced-motion: no-preference)', (context) => {
    active = true;
    ctx = context;
    initScope(document);
    return () => {
      /* media flipped to reduce: matchMedia reverts every tween/set;
         clear the guards so a flip back rebuilds cleanly */
      active = false;
      document.querySelectorAll('[data-motion-on]').forEach((el) => delete el.dataset.motionOn);
      floaters.length = 0;
    };
  });

  /* re-init per section swap via the shared registry (theme.js runs it) */
  (window.tgpcInit = window.tgpcInit || []).push((scope) => {
    if (scope !== document) initScope(scope);
  });

  if (editor) {
    document.addEventListener('shopify:section:reorder', () => ScrollTrigger.refresh());
    document.addEventListener('shopify:section:select', () => ScrollTrigger.refresh());
  }
})();
